import { Role } from '../../types/cms.types';
import { RoleService, RoleFilter, CreateRoleRequest, UpdateRoleRequest } from '../../service/supabase/RoleService';
import IBloc from '../../base/IBloc';

export interface RoleViewState {
    roles: Role[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    loading: boolean;
    error: string | null;
    filters: RoleFilter;
    hasData: boolean;
    availableModules: any[];
    availableTemplates: any[];
}

export class BlocManagementRoles extends IBloc {
    private roleService: RoleService;
    
    /**
     * State chính của Roles Management
     */
    info: RoleViewState = {
        roles: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        loading: false,
        error: null,
        filters: {},
        hasData: false,
        availableModules: [],
        availableTemplates: []
    };

    constructor() {
        super();
        this.roleService = new RoleService();
    }

    /**
     * Khởi tạo dữ liệu
     */
    async initData(): Promise<void> {
        try {
            this.setLoading(true);
            this.setError(null);

            // Load roles và metadata song song
            const [rolesResult, modules, templates] = await Promise.all([
                this.roleService.getRoles(1, 10),
                Promise.resolve(this.roleService.getAvailableModules()),
                Promise.resolve(this.roleService.getAvailableTemplates())
            ]);

            this.updateViewState({
                roles: rolesResult.data,
                pagination: {
                    total: rolesResult.total,
                    page: rolesResult.page,
                    limit: rolesResult.limit,
                    totalPages: rolesResult.totalPages
                },
                hasData: rolesResult.data.length > 0,
                availableModules: modules,
                availableTemplates: templates
            });

        } catch (error) {
            console.error('Error in initData:', error);
            this.setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi tải dữ liệu');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Tải lại dữ liệu roles
     */
    async loadRoles(page?: number, limit?: number): Promise<void> {
        try {
            this.setLoading(true);
            this.setError(null);

            const currentPage = page || this.info.pagination.page;
            const currentLimit = limit || this.info.pagination.limit;

            const result = await this.roleService.getRoles(
                currentPage,
                currentLimit,
                this.info.filters
            );

            this.updateViewState({
                roles: result.data,
                pagination: {
                    total: result.total,
                    page: result.page,
                    limit: result.limit,
                    totalPages: result.totalPages
                },
                hasData: result.data.length > 0
            });

        } catch (error) {
            console.error('Error in loadRoles:', error);
            this.setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi tải danh sách vai trò');
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Thay đổi trang
     */
    async changePage(page: number): Promise<void> {
        if (page === this.info.pagination.page) return;
        await this.loadRoles(page);
    }

    /**
     * Thay đổi số lượng items per page
     */
    async changePageSize(limit: number): Promise<void> {
        if (limit === this.info.pagination.limit) return;
        await this.loadRoles(1, limit); // Reset to page 1 when changing page size
    }

    /**
     * Tìm kiếm
     */
    async search(searchTerm: string): Promise<void> {
        const newFilters = { ...this.info.filters, search: searchTerm || undefined };
        this.updateViewState({ filters: newFilters });
        await this.loadRoles(1); // Reset to page 1 when searching
    }

    /**
     * Xóa tất cả filters
     */
    async clearFilters(): Promise<void> {
        this.updateViewState({ filters: {} });
        await this.loadRoles(1);
    }

    /**
     * Tạo role mới
     */
    async createRole(request: CreateRoleRequest): Promise<Role> {
        try {
            this.setLoading(true);
            this.setError(null);

            const newRole = await this.roleService.createRole(request);
            
            // Reload data để cập nhật danh sách
            await this.loadRoles();

            return newRole;

        } catch (error) {
            console.error('Error in createRole:', error);
            const errorMessage = error instanceof Error ? error.message : 'Có lỗi xảy ra khi tạo vai trò';
            this.setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Tạo role từ template
     */
    async createRoleFromTemplate(templateName: string): Promise<Role> {
        try {
            this.setLoading(true);
            this.setError(null);

            const newRole = await this.roleService.createRoleFromTemplate(templateName);
            
            // Reload data để cập nhật danh sách
            await this.loadRoles();

            return newRole;

        } catch (error) {
            console.error('Error in createRoleFromTemplate:', error);
            const errorMessage = error instanceof Error ? error.message : 'Có lỗi xảy ra khi tạo vai trò từ mẫu';
            this.setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Cập nhật role
     */
    async updateRole(request: UpdateRoleRequest): Promise<Role> {
        try {
            this.setLoading(true);
            this.setError(null);

            const updatedRole = await this.roleService.updateRole(request);
            
            // Reload data để cập nhật danh sách
            await this.loadRoles();

            return updatedRole;

        } catch (error) {
            console.error('Error in updateRole:', error);
            const errorMessage = error instanceof Error ? error.message : 'Có lỗi xảy ra khi cập nhật vai trò';
            this.setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Xóa role
     */
    async deleteRole(id: string): Promise<boolean> {
        try {
            this.setLoading(true);
            this.setError(null);

            const result = await this.roleService.deleteRole(id);
            
            // Reload data để cập nhật danh sách
            await this.loadRoles();

            return result;

        } catch (error) {
            console.error('Error in deleteRole:', error);
            const errorMessage = error instanceof Error ? error.message : 'Có lỗi xảy ra khi xóa vai trò';
            this.setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Lấy chi tiết role
     */
    async getRoleById(id: string): Promise<Role | null> {
        try {
            return await this.roleService.getRoleById(id);
        } catch (error) {
            console.error('Error in getRoleById:', error);
            this.setError(error instanceof Error ? error.message : 'Có lỗi xảy ra khi lấy thông tin vai trò');
            return null;
        }
    }

    /**
     * Khởi tạo roles mặc định
     */
    async initializeDefaultRoles(): Promise<void> {
        try {
            this.setLoading(true);
            this.setError(null);

            await this.roleService.initializeDefaultRoles();
            
            // Reload data để cập nhật danh sách
            await this.loadRoles();

        } catch (error) {
            console.error('Error in initializeDefaultRoles:', error);
            const errorMessage = error instanceof Error ? error.message : 'Có lỗi xảy ra khi khởi tạo vai trò mặc định';
            this.setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * Getter cho current pagination
     */
    get currentPagination() {
        return this.info.pagination;
    }

    /**
     * Getter cho current filters
     */
    get currentFilters() {
        return this.info.filters;
    }

    /**
     * Getter cho available modules
     */
    get availableModules() {
        return this.info.availableModules;
    }

    /**
     * Getter cho available templates
     */
    get availableTemplates() {
        return this.info.availableTemplates;
    }

    /**
     * Private helper methods
     */
    private setLoading(loading: boolean): void {
        this.updateViewState({ loading });
    }

    private setError(error: string | null): void {
        this.updateViewState({ error });
    }

    private updateViewState(updates: Partial<RoleViewState>): void {
        this.info = { ...this.info, ...updates };
        this.setStream('info', this.info);
    }
}
